// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Login Page
 *
 * With CIAM authentication, this page acts as a gateway:
 * - If the user is already authenticated (MSAL has an active account),
 *   redirect to the app.
 * - Otherwise, trigger a redirect to the Azure Entra External ID login page.
 *
 * The email/password form has been replaced by the CIAM redirect flow, so the
 * "Sign In" button hands off to the CIAM-hosted login screen rather than
 * collecting credentials here. Styling mirrors the Helios Operator Console
 * mockup and uses IBM Carbon components.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { Button, InlineNotification } from '@carbon/react';
import { ArrowRight } from '@carbon/icons-react';
import { loginRequest } from '../config/authConfig';
import { NEXT_PARAM, consumeReturnPath, safeReturnPath } from '../utils/returnPath';
import HeliosMark from '../components/shell/HeliosMark';
import './Login.css';
import EnergyFluoWordmark from '../components/shell/EnergyFluoWordmark';

function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAuthenticated = useIsAuthenticated();
  const { instance } = useMsal();
  const [error, setError] = useState<string | null>(null);

  // Where to land after sign-in lives in the URL (`?next=`), so a refresh of
  // this page keeps it. See utils/returnPath.ts.
  const next = safeReturnPath(searchParams.get(NEXT_PARAM));

  // A session-expiry sign-out stashed the page it left from; fold that into
  // `?next=` on arrival so the URL is the only record from here on.
  useEffect(() => {
    const stashed = safeReturnPath(consumeReturnPath());
    if (stashed && !next) {
      navigate(`/login?${NEXT_PARAM}=${encodeURIComponent(stashed)}`, { replace: true });
    }
  }, [next, navigate]);

  // Already signed in (e.g. someone opened /login directly): go on.
  useEffect(() => {
    if (isAuthenticated) navigate(next ?? '/', { replace: true });
  }, [isAuthenticated, navigate, next]);

  // Hand off to CIAM. `redirectStartPage` is where MSAL returns afterwards —
  // the requested page itself, not this one, so the round-trip lands there
  // directly instead of depending on anything this page remembers.
  // `prompt: 'login'` because anyone on this page clicked Sign In after a
  // logout: without it CIAM completes silent SSO against a cookie that an
  // abandoned sign-out (browser Back on the account picker) left alive, and
  // the user is signed back in without a password (ADO 30117). The silent
  // fallback in getTokenAsync keeps the default so a live session still SSOs.
  const handleLogin = async () => {
    try {
      await instance.loginRedirect({
        ...loginRequest,
        prompt: 'login',
        redirectStartPage: `${window.location.origin}${next ?? '/'}`,
      });
    } catch (err) {
      setError((err as Error).message || 'Failed to initiate sign-in. Please try again.');
    }
  };

  // Don't render the login UI if we're already authenticated (avoids flash)
  if (isAuthenticated) {
    return null;
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-header">
          <HeliosMark size={32} className="login-mark" />
          <p className="login-eyebrow">Sign in to</p>
          {/* The wordmark is the brand name, so it stays the page's h1 and
              carries the accessible name the text used to provide. */}
          <h1 className="login-brand">
            <EnergyFluoWordmark height={34} title="Energy Fluo" />
          </h1>
        </div>

        {error && (
          <InlineNotification
            className="login-error"
            kind="error"
            lowContrast
            title="Sign-in failed"
            subtitle={error}
            onCloseButtonClick={() => setError(null)}
          />
        )}

        <p className="login-info">Sign in with your organizational account to continue.</p>

        <Button className="login-button" renderIcon={ArrowRight} onClick={handleLogin}>
          Sign In
        </Button>
      </div>
    </div>
  );
}

export default Login;
